import React, { type FC, useCallback, useState } from 'react';

import AttachmentsViewer from './AttachmentsViewer';
import { useAttachmentsViewerContext } from './context';
import { DOWNLOAD_METHODS } from '../../consts';
import { useAttachmentsContext } from '../../context/AttachmentsContext';
import useAttachmentsViewerKeyboardNavigation from '../../hooks/useAttachmentsViewerKeyboardNavigation';
import usePreview from '../../hooks/usePreview';
import AttachmentDeleteConfirmationModal from '../common/AttachmentDeleteConfirmationModal';

const ConnectedAttachmentsViewer: FC = () => {
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);

  const {
    open,
    viewNextAttachment,
    viewPreviousAttachment,
    currentAttachment,
    closeViewer,
    totalAttachments,
    currentAttachmentIndex,
    zoomIn,
    zoomOut,
    currentZoomLevel,
  } = useAttachmentsViewerContext();

  const { deleteAttachment, downloadAttachment, allowDelete, readOnly } =
    useAttachmentsContext();

  useAttachmentsViewerKeyboardNavigation();

  const { loading, preview, type } = usePreview({
    attachmentId: currentAttachment?.attachmentId,
  });

  const onDownload = useCallback(() => {
    if (currentAttachment) {
      void downloadAttachment({
        attachmentId: currentAttachment.attachmentId,
        method: DOWNLOAD_METHODS.VIEWER,
      });
    }
  }, [currentAttachment, downloadAttachment]);

  const onDeleteConfirm = useCallback(() => {
    if (currentAttachment) {
      void deleteAttachment(currentAttachment.attachmentId);
      closeViewer();
    }
  }, [deleteAttachment, closeViewer, currentAttachment]);

  if (!currentAttachment) {
    return null;
  }

  return (
    <>
      <AttachmentsViewer
        filename={currentAttachment.displayName}
        open={open}
        onClose={closeViewer}
        onDownload={onDownload}
        onDelete={() => {
          setIsDeleteConfirmationModalOpen(true);
        }}
        createdOn={currentAttachment.createdOn}
        fileSize={currentAttachment.fileSize}
        createdByName={currentAttachment.createdByName}
        createdBy={currentAttachment.createdBy}
        onNext={viewNextAttachment}
        onPrevious={viewPreviousAttachment}
        totalAttachments={totalAttachments}
        currentIndex={currentAttachmentIndex + 1}
        zoomIn={zoomIn}
        zoomOut={zoomOut}
        currentZoomLevel={currentZoomLevel}
        preview={preview}
        loading={loading}
        type={type}
        allowDelete={allowDelete && !readOnly}
      />
      <AttachmentDeleteConfirmationModal
        onClose={() => setIsDeleteConfirmationModalOpen(false)}
        onConfirm={onDeleteConfirm}
        open={isDeleteConfirmationModalOpen}
        fileName={currentAttachment.displayName}
      />
    </>
  );
};

export default ConnectedAttachmentsViewer;
